export const POST_QUERY_KEYS = {
  root: ['posts'] as const,
  byId: (communityId: string, postId: string) =>
    [...POST_QUERY_KEYS.root, communityId, postId] as const,
  bySlug: (communityId: string, slug: string) =>
    [...POST_QUERY_KEYS.root, communityId, 'slug', slug] as const,
}

export const COMMUNITY_QUERY_KEYS = {
  root: ['communities'] as const,
  byPath: (path: string) =>
    [...COMMUNITY_QUERY_KEYS.root, 'path', path] as const,
  member: (communityId: string) =>
    [...COMMUNITY_QUERY_KEYS.root, communityId, 'member'] as const,
}

export const COURSES_QUERY_KEYS = {
  root: ['courses'] as const,
  list: (communityId: string, published: boolean = false) =>
    [...COURSES_QUERY_KEYS.root, communityId, published] as const,
}

export const EVENTS_QUERY_KEYS = {
  root: ['events'] as const,
  list: (communityId: string) =>
    [...EVENTS_QUERY_KEYS.root, communityId, 'list'] as const,
  // Always "upcoming", independent of whatever sort tab the events page is
  // currently on — used by the feed sidebar's preview widget so it doesn't
  // inherit the page's shared sort-strategy state.
  preview: (communityId: string) =>
    [...EVENTS_QUERY_KEYS.root, communityId, 'preview'] as const,
}

export const PREVIEW_QUERY_KEYS = {
  root: ['preview'] as const,
  byCommunity: (communityId: string) =>
    [...PREVIEW_QUERY_KEYS.root, communityId] as const,
}

export const INVITE_LINKS_QUERY_KEYS = {
  root: ['invite-links'] as const,
  list: (communityId: string) =>
    [...INVITE_LINKS_QUERY_KEYS.root, communityId] as const,
}

export const PAYMENT_METHODS_QUERY_KEYS = {
  root: ['payment-methods'] as const,
  list: () => [...PAYMENT_METHODS_QUERY_KEYS.root, 'list'] as const,
}

export const USER_CARDS_QUERY_KEYS = {
  root: ['user-cards'] as const,
  list: () => [...USER_CARDS_QUERY_KEYS.root, 'list'] as const,
}

export const USER_SUBSCRIPTIONS_QUERY_KEYS = {
  root: ['user-subscriptions'] as const,
  list: () => [...USER_SUBSCRIPTIONS_QUERY_KEYS.root, 'list'] as const,
}

export const NOTIFICATION_SETTINGS_QUERY_KEYS = {
  root: ['notification-settings'] as const,
  byCommunity: (communityId?: string | null) =>
    [...NOTIFICATION_SETTINGS_QUERY_KEYS.root, communityId ?? 'global'] as const,
}

export const FEED_QUERY_KEYS = {
  root: ['feed'] as const,
  list: (communityId: string) =>
    [...FEED_QUERY_KEYS.root, communityId] as const,
}

export const NOTIFICATIONS_QUERY_KEYS = {
  root: ['notifications'] as const,
  list: () => [...NOTIFICATIONS_QUERY_KEYS.root, 'list'] as const,
  unreadCount: () => [...NOTIFICATIONS_QUERY_KEYS.root, 'unread-count'] as const,
}

export const COMMUNITY_LIST_QUERY_KEYS = {
  root: ['community-list'] as const,
  communities: (userId: string) =>
    [...COMMUNITY_LIST_QUERY_KEYS.root, userId] as const,
}

export const COMMUNITY_SELECT_QUERY_KEYS = {
  root: ['community-select'] as const,
  list: (userId: string, search: string) =>
    [...COMMUNITY_SELECT_QUERY_KEYS.root, userId, search] as const,
}

export const PROFILE_QUERY_KEYS = {
  root: ['profile'] as const,
  byNickname: (nickname: string) =>
    [...PROFILE_QUERY_KEYS.root, nickname] as const,
}

export const COURSE_QUERY_KEYS = {
  root: ['course'] as const,
  byId: (communityId: string, courseId: string) =>
    [...COURSE_QUERY_KEYS.root, communityId, courseId] as const,
  bySlug: (communityId: string, slug: string) =>
    [...COURSE_QUERY_KEYS.root, communityId, 'slug', slug] as const,
}

export const COURSE_ENTITY_QUERY_KEYS = {
  root: ['course-entity'] as const,
  byId: (communityId: string, entityId: string) =>
    [...COURSE_ENTITY_QUERY_KEYS.root, communityId, entityId] as const,
  bySlug: (communityId: string, courseId: string, slug: string) =>
    [...COURSE_ENTITY_QUERY_KEYS.root, communityId, courseId, 'slug', slug] as const,
}

export const COMMENTS_QUERY_KEYS = {
  root: ['comments'] as const,
  list: (parentType: string, parentId: string, sortOrder: string) =>
    [...COMMENTS_QUERY_KEYS.root, parentType, parentId, sortOrder] as const,
}

export const CHAT_QUERY_KEYS = {
  root: ['chat'] as const,
  conversations: () => [...CHAT_QUERY_KEYS.root, 'conversations'] as const,
  conversation: (id: string) => [...CHAT_QUERY_KEYS.root, 'conversation', id] as const,
  messages: (convId: string) => [...CHAT_QUERY_KEYS.root, 'messages', convId] as const,
  unread: () => [...CHAT_QUERY_KEYS.root, 'unread'] as const,
  presence: () => [...CHAT_QUERY_KEYS.root, 'presence'] as const,
  settings: () => [...CHAT_QUERY_KEYS.root, 'settings'] as const,
  partnerCommunities: (userId: string) =>
    [...CHAT_QUERY_KEYS.root, 'partner-communities', userId] as const,
  blocks: () => [...CHAT_QUERY_KEYS.root, 'blocks'] as const,
  userSearch: (q: string) => [...CHAT_QUERY_KEYS.root, 'user-search', q] as const,
}

/**
 * DEV-616. Свой корень, а не ветка внутри `chat`: `resetState` чат-стора
 * сносит весь кеш по `CHAT_QUERY_KEYS.root`, и обсуждения улетали бы вместе с ним.
 */
export const DISCUSSION_QUERY_KEYS = {
  root: ['discussions'] as const,
  list: (communityId: string) =>
    [...DISCUSSION_QUERY_KEYS.root, 'list', communityId] as const,
  /**
   * Лента обсуждения. С `anchorId` — отдельное окно вокруг конкретного
   * сообщения: у него свой кеш, чтобы прыжок в прошлое не затирал уже
   * загруженный «хвост», и возврат к свежим был мгновенным.
   */
  messages: (conversationId: string, anchorId?: string | null) =>
    anchorId
      ? ([
          ...DISCUSSION_QUERY_KEYS.root,
          'messages',
          conversationId,
          'around',
          anchorId,
        ] as const)
      : ([...DISCUSSION_QUERY_KEYS.root, 'messages', conversationId] as const),
  postMessages: (postId: string) =>
    [...DISCUSSION_QUERY_KEYS.root, 'post-messages', postId] as const,
  pinned: (conversationId: string) =>
    [...DISCUSSION_QUERY_KEYS.root, 'pinned', conversationId] as const,
  /** DEV-634: заглушённые обсуждения сообщества — по ним не приходят ни пуши, ни уведомления. */
  mutes: (communityId: string) =>
    [...DISCUSSION_QUERY_KEYS.root, 'mutes', communityId] as const,
}

export const API_KEYS_QUERY_KEYS = {
  root: ["apiKeys"],
  list: (communityId: string) => ["apiKeys", "list", communityId],
};

export const AUTOMATION_QUERY_KEYS = {
  root: ["automation"],
  overview: (communityId: string) => ["automation", "overview", communityId],
  series: (communityId: string, days: number) => [
    "automation",
    "series",
    communityId,
    days,
  ],
  journal: (communityId: string) => ["automation", "journal", communityId],
};


export const JOIN_REQUESTS_QUERY_KEYS = {
  root: ['join-requests'] as const,
  myRequest: (communityId: string) =>
    [...JOIN_REQUESTS_QUERY_KEYS.root, communityId, 'my'] as const,
}


export const COURSE_GROUP_QUERY_KEYS = {
  root: ["course-groups"] as const,
  list: (communityId: string) =>
    [...COURSE_GROUP_QUERY_KEYS.root, communityId] as const,
};

export const TARIFF_QUERY_KEYS = {
  root: ['tariffs'] as const,
  active: (communityId: string) =>
    [...TARIFF_QUERY_KEYS.root, communityId, 'active'] as const,
};
